import {pagesPath} from '@/ts/router/pages-path';
import {
  computed,
  type ComputedRef,
} from 'vue';
import {useInterventionStore} from '@/ts/store/intervention-store';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';
import {getVueLink} from '@/ts/composables/stateful/use-route-params';
import {
  PublicationState,
  SessionState,
} from '@/ts/types/dto/interventions-dto';
import {useIntervention} from '@/ts/composables/stateful/use-intervention';
import {usePageId} from '@/ts/composables/stateful/use-page';
import {
  useSessionId,
  useSessionProgress,
} from '@/ts/composables/stateful/use-session';
import type {InterventionInProgress} from '@/ts/types/dto/home-dto';

/** Provides computed navigation links for the current intervention (about, sessions, current session, recap) */
export function useInterventionLinks(): {
  aboutLink: ComputedRef<string | null>;
  sessionsLink: ComputedRef<string | null>;
  currentSessionLink: ComputedRef<string | null>;
  recapLink: ComputedRef<string | null>;
} {
  const st = useInterventionStore();
  const {currentSession} = useIntervention();
  const aboutLink = computed(() => {
    return st.value ? getVueLink(pagesPath.intervention.interventionInfoAbout, {iid: st.value!.id}) : null;
  });
  const sessionsLink = computed(() => {
    return st.value ? getVueLink(pagesPath.intervention.interventionInfoSessions, {iid: st.value!.id}) : null;
  });
  const currentSessionLink = computed(() => {
    return st.value ? getVueLink(
      pagesPath.session.sessionPage,
      {
        iid: st.value!.id,
        sid: currentSession.value!.id,
        pid: 1,
      },
    ) : null;
  });
  const recapLink = computed(() => {
    return st.value ? getVueLink(pagesPath.intervention.interventionRecap, {iid: st.value!.id}) : null;
  });
  return {aboutLink, sessionsLink, currentSessionLink, recapLink};
}

/** Resolves the next navigation link from a session page (next page, next session, or back to landing) */
export function useNextLink(
  sid: ComputedRef<UuidDTO>,
  pid: ComputedRef<number>,
): ComputedRef<string> {
  const st = useInterventionStore();
  const {nextPageId} = usePageId(sid, pid);
  const {session} = useSessionId(sid);

  return computed(() => {
    // if upcoming page in current session available, jump to next page
    if (nextPageId.value) {
      return getVueLink(
        pagesPath.session.sessionPage,
        {
          pid: nextPageId.value!,
          sid: sid.value!,
          iid: st.value!.id,
        },
      );
    }

    // If this is the last page of a completed session, go to completed page with just_completed flag
    if (session.value?.state === SessionState.COMPLETED &&
        pid.value === session.value.pages.length) {
      return `${getVueLink(pagesPath.session.sessionCompletedPage, {
        iid: st.value!.id,
        sid: sid.value!,
      })  }?just_completed=true`;
    }

    // no upcoming page, jump to page one of next session
    const currentSessionIndex =  st.value?.sessions.findIndex((item) => item.id === sid.value);
    const nextSessionIndex = Number.isInteger(currentSessionIndex) ? currentSessionIndex! + 1 : null;

    if (Number.isInteger(nextSessionIndex) && Array.isArray(st.value?.sessions) && st.value?.sessions.length > nextSessionIndex!) {
      return getVueLink(pagesPath.session.sessionPage, {
        iid: st.value!.id,
        sid: st.value!.sessions[nextSessionIndex!].id,
        pid: 1,
      });
    }

    // no upcoming session, jump back to intervention landing page
    return getVueLink(pagesPath.intervention.interventionLanding, {
      iid: st.value!.id,
    });
  });
}

/** Computes the link to the current page of an in-progress intervention based on session state */
export function useCurrentPageLink(
  props: {
    intervention: InterventionInProgress | null
  },
): ComputedRef<string | null> {
  return computed(() => {
    if (!props.intervention) {
      return null;
    }
    if (!props.intervention.currentSessionState) {
      return getVueLink(pagesPath.intervention.interventionInfoAbout, {iid: props.intervention.interventionId});
    }
    if ([SessionState.LOCK, SessionState.HOMEWORK_PENDING].includes(props.intervention.currentSessionState)) {
      return getVueLink(pagesPath.session.sessionPage, {
        iid: props.intervention.interventionId,
        sid: props.intervention.currentSessionId!,
        pid: 1,
      });
    }
    return getVueLink(pagesPath.session.sessionPage, {
      iid: props.intervention.interventionId,
      sid: props.intervention.currentSessionId!,
      // e.g. if lastPageIndexCompleted == 1 is completed,
      // means first 2 are completed and current is 3rd
      pid: props.intervention.currentPageIndex! + 1,
    });
  });
}

/** Provides summary and current page links for a session by its ID */
export function useSessionsLinksId(id: ComputedRef<UuidDTO | null>): {
  summaryLink: ComputedRef<string>;
  currentPageLink: ComputedRef<string | null>;
} {
  const {session} = useSessionId(id);
  const {currentPageId} = useSessionProgress(id);
  const intervention = useInterventionStore();
  const summaryLink = computed(() => {
    // If session is completed, navigate to dedicated completed sessions page
    if (session.value?.state === SessionState.COMPLETED) {
      return getVueLink(pagesPath.session.sessionCompletedPage, {
        iid: intervention.value!.id,
        sid: session.value!.id,
      });
    }

    // Current behavior for non-completed sessions
    return getVueLink(pagesPath.session.sessionPage, {
      iid: intervention.value!.id,
      sid: session.value!.id,
      pid: 1,
    });
  });
  const currentPageLink = computed(() => {
    if (intervention.value?.publicationState === PublicationState.COMPLETED) {
      return getVueLink(pagesPath.session.sessionPage, {
        iid: intervention.value!.id,
        sid: intervention.value?.sessions[0]?.id,
        pid: 1,
      });
    }
    if ((!currentPageId.value) || !intervention.value || intervention.value!.publicationState !== PublicationState.IN_PROGRESS) {
      return null;
    }
    // includes undefined return false
    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
    if ([SessionState.LOCK, SessionState.HOMEWORK_PENDING].includes(session.value?.state!)) {
      return getVueLink(pagesPath.session.sessionPage, {
        iid: intervention.value!.id,
        sid: session.value!.id,
        pid: 1,
      });
    }
    return getVueLink(pagesPath.session.sessionPage, {
      iid: intervention.value!.id,
      sid: session.value!.id,
      pid: currentPageId.value!,
    });
  });
  return {summaryLink, currentPageLink};
}
